/**
 * Optional wait on a dependency condition.
 * Stored as waitSeconds. Older saves used waitMinutes — still read.
 */

import { parseMissionDate } from '../../../../../utils/missionDates';

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86400;

/**
 * @param {unknown} value
 * @returns {number|null} positive whole seconds, or null
 */
export function normalizeWaitSeconds(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

/**
 * Read wait from a condition. Prefers waitSeconds; falls back to waitMinutes.
 * @param {object|string|null|undefined} cond
 * @returns {number|null}
 */
export function getWaitSeconds(cond) {
  if (!cond || typeof cond === 'string') return null;
  const fromSeconds = normalizeWaitSeconds(cond.waitSeconds);
  if (fromSeconds) return fromSeconds;
  const minutes = normalizeWaitSeconds(cond.waitMinutes);
  return minutes ? minutes * SECONDS_PER_MINUTE : null;
}

/**
 * @param {unknown} seconds
 * @returns {{ d: string, h: string, m: string, s: string }}
 */
export function splitSecondsToDHMS(seconds) {
  const n = normalizeWaitSeconds(seconds);
  if (!n) return { d: '', h: '', m: '', s: '' };
  const d = Math.floor(n / SECONDS_PER_DAY);
  let rem = n % SECONDS_PER_DAY;
  const h = Math.floor(rem / SECONDS_PER_HOUR);
  rem %= SECONDS_PER_HOUR;
  const m = Math.floor(rem / SECONDS_PER_MINUTE);
  const s = rem % SECONDS_PER_MINUTE;
  return {
    d: d > 0 ? String(d) : '',
    h: h > 0 ? String(h) : '',
    m: m > 0 ? String(m) : '',
    s: s > 0 ? String(s) : '',
  };
}

/**
 * @param {{ d?: string|number, h?: string|number, m?: string|number, s?: string|number }} parts
 * @returns {number|null}
 */
export function joinDHMSToSeconds(parts) {
  const num = (v) => {
    const n = parseInt(String(v ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const total =
    num(parts.d) * SECONDS_PER_DAY +
    num(parts.h) * SECONDS_PER_HOUR +
    num(parts.m) * SECONDS_PER_MINUTE +
    num(parts.s);
  return total > 0 ? total : null;
}

/**
 * Compact duration: 30s, 30m, 24h, 2d, "1d 2h", "1m 30s".
 * Whole hours under 2 days stay as hours so "24h" stays "24h".
 * @param {unknown} seconds
 * @returns {string|null}
 */
export function formatWaitDuration(seconds) {
  const n = normalizeWaitSeconds(seconds);
  if (!n) return null;
  if (n % SECONDS_PER_HOUR === 0 && n < SECONDS_PER_DAY * 2) {
    return `${n / SECONDS_PER_HOUR}h`;
  }
  const { d, h, m, s } = splitSecondsToDHMS(n);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s) parts.push(`${s}s`);
  return parts.join(' ') || null;
}

/**
 * Spoken duration for prose: "5 days", "24 hours", "1 minute 30 seconds".
 * Same unit grouping as formatWaitDuration (hours under 2 days stay hours).
 */
export function formatWaitDurationWords(seconds) {
  const n = normalizeWaitSeconds(seconds);
  if (!n) return null;
  const unit = (count, word) => `${count} ${word}${Number(count) === 1 ? '' : 's'}`;
  if (n % SECONDS_PER_HOUR === 0 && n < SECONDS_PER_DAY * 2) {
    return unit(n / SECONDS_PER_HOUR, 'hour');
  }
  const { d, h, m, s } = splitSecondsToDHMS(n);
  const parts = [];
  if (d) parts.push(unit(d, 'day'));
  if (h) parts.push(unit(h, 'hour'));
  if (m) parts.push(unit(m, 'minute'));
  if (s) parts.push(unit(s, 'second'));
  return parts.join(' ') || null;
}

/**
 * @param {unknown} seconds
 * @param {string} missionName
 * @returns {string|null} e.g. "24h after Send newsletter"
 */
export function formatWaitAfterHint(seconds, missionName) {
  const dur = formatWaitDuration(seconds);
  const name = typeof missionName === 'string' ? missionName.trim() : '';
  if (!dur || !name) return null;
  return `${dur} after ${name}`;
}

/**
 * Collect wait hints from stored v2 conditions.
 * @param {{ blocks?: Array<{ conditions?: Array }> }|null|undefined} unlockConditions
 * @param {Array<{ id: string, name?: string }>} allTasks
 * @returns {string[]}
 */
export function collectWaitHints(unlockConditions, allTasks = []) {
  const blocks = unlockConditions?.blocks;
  if (!Array.isArray(blocks)) return [];

  const hints = [];
  for (const block of blocks) {
    for (const cond of block.conditions || []) {
      const wait = getWaitSeconds(cond);
      if (!wait) continue;
      const name = allTasks.find((t) => t.id === cond.taskId)?.name || 'this mission';
      const hint = formatWaitAfterHint(wait, name);
      if (hint) hints.push(hint);
    }
  }
  return hints;
}

/**
 * When the wait clock started: predecessor completedAt, else updatedAt.
 * @param {object|null|undefined} predecessor
 * @returns {number|null} epoch ms
 */
export function getWaitClockStartMs(predecessor) {
  if (!predecessor) return null;
  const start = parseMissionDate(predecessor.completedAt) || parseMissionDate(predecessor.updatedAt);
  return start ? start.getTime() : null;
}

/**
 * @param {object} condition
 * @param {object|null|undefined} predecessor
 * @returns {number|null} epoch ms when the wait elapses
 */
export function getConditionAvailableAtMs(condition, predecessor) {
  const wait = getWaitSeconds(condition);
  if (!wait) return null;
  const start = getWaitClockStartMs(predecessor);
  if (start == null) return null;
  return start + wait * 1000;
}

/** True when lockReason is the wait-only countdown (old "Starts in" still matches). */
export function isWaitCountdownReason(lockReason) {
  return typeof lockReason === 'string' && /^(Available in|Starts in)\b/.test(lockReason);
}

/** True when lockReason is a pending wait ("24h after Send newsletter") — clock not started yet. */
export function isPendingWaitHintReason(lockReason) {
  return typeof lockReason === 'string' && /^\d+[dhms]\b/.test(lockReason) && / after /.test(lockReason);
}

/** Status pill should show the wait inside the box for both ticking and pending waits. */
export function isWaitStatusReason(lockReason) {
  return isWaitCountdownReason(lockReason) || isPendingWaitHintReason(lockReason);
}

/**
 * Copy for "you clicked status while only the clock is left."
 * @returns {{ title: string, clauses: Array<{ duration: string|null, name: string }>, verb: string }|null}
 */
export function formatWaitAvailabilityCopy(blockers) {
  const waits = (blockers || []).filter((d) => d.reason === 'waiting_until');
  if (!waits.length) return null;
  const clauses = waits.map((d) => ({
    duration: formatWaitDurationWords(d.waitSeconds),
    name: String(d.name || '').trim() || 'a previous mission',
  }));
  return {
    title: "You can't start this mission yet, there's a timer on it",
    clauses,
    verb: waits.length === 1 ? 'has' : 'have',
  };
}

/** Remaining wait as compact clock text, e.g. "18h 12m". */
export function formatWaitCountdown(remainingMs) {
  if (remainingMs == null || remainingMs <= 0) return null;
  const totalSec = Math.max(1, Math.ceil(remainingMs / 1000));
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return h ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

function eachStoredCondition(task, visit) {
  const uc = task?.unlockConditions;
  if (uc && typeof uc === 'object' && !Array.isArray(uc) && uc.version === 2) {
    for (const block of uc.blocks || []) {
      for (const cond of block.conditions || []) visit(cond);
    }
    return;
  }
  if (Array.isArray(uc)) {
    for (const cond of uc) visit(cond);
    return;
  }
  if (Array.isArray(task?.dependsOn)) {
    for (const dep of task.dependsOn) visit(typeof dep === 'object' ? dep : {});
  }
}

/** True if any mission in the list has a wait on a dependency. */
export function tasksHaveWaitGates(tasks) {
  if (!Array.isArray(tasks)) return false;
  for (const task of tasks) {
    let found = false;
    eachStoredCondition(task, (cond) => {
      if (getWaitSeconds(cond)) found = true;
    });
    if (found) return true;
  }
  return false;
}
